/**
 * 分类契约 —— 列表 / 选择器（**B 域** · B3 分类体系 + B4 冷启动与渐进披露）
 *
 * 依据：B 域 PRD **§5.3**（分类体系）· **§5.4**（冷启动与渐进披露 · `AC-B4.1`~`AC-B4.8`）·
 * 规格 **§22**（分类）· `DR-007`（预设分类数值 · 16+8 L1）· `DR-018` · `DR-021` §6 ·
 * 架构 **§4.6**（身份层 × 呈现层分工）· **§6.1**（契约唯一源）。
 *
 * ## 🔴 本文件为什么这么小（以及为什么不含任何中文名）
 *
 * 分类的**内容**（`id` / `name` / 层级 / 顺序 / 图标绑定）**不在契约层** ——
 * 它在**身份层**（`enums/categories.ts`，业务唯一源）与**呈现层**（设计资产）合并成的产物里，
 * 由服务端从**账本级 `categories` 表**读出后下发（`GET /api/categories`）。
 * ⇒ 契约层只描述**形状**。🔴 **任何地方都不得出现分类中文名字面量**
 * （架构 §4.6 铁律② · 执行载体 = ESLint `local/no-cat-name-literal`；代码引用分类只能走 `CAT.*`）。
 *
 * ## 🔴 `common` 为什么由服务端下发，而不是端侧自己筛
 *
 * B 域 PRD **§5.3.5「跨端单源」**要求 Web 与小程序看到**同一份**常用集。若两端各自
 * `COMMON_L1_IDS.includes(...)`，将来 `AC-B4.8`（**用户自定义**常用集合）落地时，
 * 两端都要改、且数据源变成"共享常量 + 用户偏好"两处拼接 ⇒ 必然漂移。
 * ⇒ 服务端算好 `common` 随列表下发，**端侧只渲染 `common === true` 的行**（对"哪些是常用"零知识）。
 *
 * ## 为什么**不**分页（与 `AccountListResult` 同因）
 *
 * 分类总量 = L1 + L2 ≈ 146 行，且**选择器必须一次拿到全量**（否则"全部 N 类"与拼音检索
 * 都搜不到非当前页的分类 —— 与账户列表同一条理由）。⚠️ 端侧切换支出/收入**不必重取**：
 * 全量已在手，按 `kind` 过滤即可。
 */

import { z } from 'zod';
import { CategoryKind } from '../enums/platform.js';

/**
 * 分类视图（**L1 与 L2 同形** —— 用 `level` / `parentId` 区分，避免两个近重复类型。
 *
 * 层级用「同一个形状 + `level` 字段」表达，与身份层 `CategoryIdentity` 一致；
 * 端侧据此一次渲染三级（常用 → 全部 L1 → 选中 L1 的 L2）。
 */
export interface CategoryView {
  /** 🔴 稳定 `id`（`cat-meal` / `cat-meal-breakfast`）—— **唯一引用键**（规格 §22.4） */
  readonly id: string;
  /** 中文名（**只作展示**；代码引用一律走 `CAT.*`） */
  readonly name: string;
  /** `expense` / `income` —— L2 恒等于父级（服务端已保证） */
  readonly kind: CategoryKind;
  /** `1` = L1（大类）· `2` = L2（子类） */
  readonly level: 1 | 2;
  /** L1 为 `null`；L2 为其 L1 的 `id`（同账本内） */
  readonly parentId: string | null;
  /** 🔴 `true` = 系统分类（禁改名禁删除 · 引擎按 `CAT.*` 引用 · 规格 §22.4） */
  readonly system: boolean;
  /** 🔴 常用集标记（B 域 PRD **§5.4.4** 拍板口径）—— 端侧据此做第 1 级露出。**只有 L1 可能为 `true`** */
  readonly common: boolean;
  /**
   * 图标绑定（**呈现层**提供，构建期合并产物）。
   * `null` = 无绑定 ⇒ 端侧走 L1 继承 / 兜底字形（不是空串 —— 空串会让"没绑定"与"绑定了空值"不可分辨）。
   */
  readonly iconToken: string | null;
  /**
   * 🆕 批 `112-B`：`true` = **已被用户移除**（`categories.hidden = 1`，`DR-062` D3 软删）。
   *
   * 🔴 **可选键、只在为 `true` 时出现**（铁律 63：可选键剔键，不发 `undefined`）——
   * 这样「未移除」是**缺键**而不是 `false`，端侧只需判 `=== true`。
   *
   * 🟢 **正常情况下这个键根本不会出现**：默认 `GET /api/categories` **不返回已移除项**
   * （`AC-B3.14`：移除后不再出现在任何选择器里）。只有显式带 `?includeHidden=1` 才可能看到，
   * 当前唯一的调用方是**流水的分类筛选**（`112-B`）—— 用户需要按历史分类筛选，
   * 否则「移除分类」会连历史都搜不到（`AC-B3.13` 的筛选侧兑现）。
   */
  readonly removed?: true;
}

/**
 * 分类列表结果。
 *
 * ⚠️ 与 `AccountListResult` / `PolicyListResult` 同为**不分页**形状 —— 见文件头说明。
 */
export interface CategoryListResult {
  /**
   * **顺序即展示顺序**（服务端已按「L1 全局序 → 其 L2 同父序」排好，即深度优先）——
   * 🔴 端侧**不得**重排（重排 = 第二份排序口径，改顺序要改身份层数组）。
   */
  readonly items: readonly CategoryView[];
  /** = `items.length` */
  readonly total: number;
}

// ───────────────────────── 自建分类（批 `112-A` · `DR-062`） ─────────────────────────

/**
 * 自建分类的**数量上限**（`DR-062` D6）。
 *
 * 🔴 为什么必须有上限：完全放开会让选择器又退回"100+ 项里找一个"，
 * 与本产品「渐进披露」的核心交互相悖（`AC-B4.4` 的初衷）。
 */
export const CUSTOM_CATEGORY_LIMITS = { l1: 20, l2: 100 } as const;

/**
 * 自建分类可用的**备用图标池**（`AC-B3.8` 的反向保护）。
 *
 * 🔴 **权威源** = 呈现层 `docs/03-UX与UI设计/assets/icons.js` 的 `CAT_ICON_SPARE`；
 * 本常量必须与它**逐字一致（11 个）**（`categories.spec` 有计数断言）。
 *
 * 🔴 它同时是**白名单**：写入路径只接受这些 token。理由不是"限制用户"，
 * 而是这 11 个是**预留给自建**的 —— 一旦允许写 `CAT_ICON` 里那 59 个已绑定图标，
 * 后来的自建就没有图标可选了（`AC-B3.8` 的镜像保护）。
 */
export const CATEGORY_SPARE_ICONS = [
  'hotel',
  'camera',
  'plant',
  'sport',
  'cake',
  'car',
  'moon',
  'star',
  'leaf',
  'sun',
  'firstaid',
] as const;

export type CategorySpareIcon = (typeof CATEGORY_SPARE_ICONS)[number];

/** 分类名长度上限（按字符计；"宠物医疗-疫苗" 这种 20 字足够） */
export const CATEGORY_NAME_MAX = 20;

const CategoryNameSchema = z
  .string()
  .trim()
  .min(1, '分类名不能为空')
  .max(CATEGORY_NAME_MAX, `分类名最多 ${String(CATEGORY_NAME_MAX)} 个字`);

/** `POST /api/categories` —— 建自建分类（`DR-062` D1/D2） */
export const CreateCategoryRequestSchema = z.object({
  /** `null` = 建 **L1**；非空 = 在该 L1 下建 **L2**（两级铁律，`AC-B3.5`） */
  parentId: z.string().min(1).nullable(),
  /**
   * 建 L1 时**必填**；建 L2 时**忽略**且由服务端强制取父级 `kind`
   * （子 ≠ 父会让分类树与报表聚合同时崩，所以不做"信任客户端"）。
   */
  kind: z.enum([CategoryKind.Expense, CategoryKind.Income] as const).optional(),
  name: CategoryNameSchema,
  /** 只能选 11 个备用图标之一（`AC-B3.8`）；`null`/缺省 = 无图标 */
  iconToken: z.enum(CATEGORY_SPARE_ICONS).nullish(),
});
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;

/**
 * `PATCH /api/categories/:id` —— 改名 / 换图标（`DR-062` D4）。
 *
 * 🔴 **不含** `parentId` / `kind` —— 移动层级会改变历史分类树的归属，
 * 属另一个量级（本批 out-of-scope，见 `DR-062` §6）。
 */
export const UpdateCategoryRequestSchema = z.object({
  name: CategoryNameSchema.optional(),
  iconToken: z.enum(CATEGORY_SPARE_ICONS).nullish(),
});
export type UpdateCategoryRequest = z.infer<typeof UpdateCategoryRequestSchema>;

/**
 * 写操作的回执：**写后的那一行**（`DR-062` D1 的"原地刷新"要求）。
 *
 * 🔴 返回整行而不是只回 id：端侧管理对话框要**原地替换**这一行，
 * 若只回 id 就得再拉一次全量（`GET /api/categories` 是 146 行）。
 */
export interface CategoryMutationResult {
  readonly category: CategoryView;
}
